import { FragmentSearchResult, BestPoseBindingInfo, sortFrags } from '../../../model/FragmentSearchResult';
import { getFragmentPropsForSuggestion, getMapCaseAndTargetInfo } from './utils';

/**
 * @typedef {import('BMapsModel').DecodedFragment} DecodedFragment
 * @typedef {import('BMapsModel').FragmentSearchResult} FragmentSearchResult
 * @typedef {{ frag: DecodedFragment, fragIndex: number, displayIndex: number }} DisplayPose
 * @typedef {{ value: any, filter: function(DecodedFragment): boolean }} PosePropertyFilter
 */

/**
 * Wrapper for FragmentSearchResult that adds fragment info,
 * annotates per-case data with target info, and calculates summary information.
 */
export class AnnotatedSearchResult {
    static getProjectCasesForSuggestions(suggestions) {
        const projectCases = new Set();

        for (const sugg of suggestions) {
            sugg.projectCases.forEach((pc) => projectCases.add(pc));
        }
        return [...projectCases];
    }

    /**
     * @param {}
     * @param {ActivityThresholdInfo?}
     */
    constructor(suggestion, activityThresholdInfo) {
        /** @type {FragmentSearchResult} */
        this.suggestion = suggestion;
        /** @type {string[]} */
        this.projectCases = Object.keys(suggestion.projectCaseDetails);
        this.fragInfo = getFragmentPropsForSuggestion(this.suggestion, this.projectCases);
        /** @type {AnnotatedBestPoseInfo?} */
        this.refBestPoseInfo = null;
        /** @type {{ [projectCase: string]: AnnotatedBestPoseInfo }} */
        this.perCaseInfo = {};
        this.updatePerCaseInfo();
        this.updateSummaryInfo(activityThresholdInfo);
        /** @type {DisplayPose[]} */
        this.displayPoses = [];
        this.assignDisplayPoses(this.frags);
        /** @type {number} */
        this.selectedIndex = -1;
    }

    // Passthroughs to the underlying result object
    get origin() { return this.suggestion.origin; }
    get modType() { return this.suggestion.modType; }
    get fragAtomName() { return this.suggestion.fragAtomName; }
    get frags() { return this.suggestion.frags; }
    set frags(value) { this.suggestion.frags = value; }
    get name() { return this.suggestion.name; }
    get mwt() { return this.suggestion.mwt; }
    get selectedFrag() { return this.frags[this.selectedIndex]; }
    get selectedDisplayPose() { return this.getDisplayPoseByFragIndex(this.selectedIndex); }
    get selectedDisplayIndex() { return this.selectedDisplayPose?.displayIndex ?? -1; }

    getFragByIndex(index) {
        return this.suggestion.frags[index];
    }

    getFragByDisplayIndex(displayIndex) {
        const displayPose = this.displayPoses[displayIndex];
        return displayPose?.frag;
    }

    getDisplayPoseByFragIndex(fragIndex) {
        const index = this.displayPoses.findIndex((d) => d.fragIndex === fragIndex);
        return this.displayPoses[index];
    }

    recalculateBestPoses({ bestPoseOrderField }={}) {
        this.suggestion.recalculateBestPoses(bestPoseOrderField);
        this.updatePerCaseInfo();
    }

    /**
     * Display poses are the visible poses that can be viewed by the selector,
     * potentially filtered and sorted. They need to be recalculated whenever the
     * view parameters change.
     * The difference between sortPosesBy and bestPoseOrderField is that
     * sortPosesBy is used to sort the display poses, and bestPoseOrderField
     * is used to determine which pose is providing the values for the row
     * in the table. For "position" sorting, the best pose is at index 0.
     * @param {{
     *     sortPosesBy: string,
     *     bestPoseOrderField: string,
     *     displayFilters: {[propName: string]: PosePropertyFilter},
     *     preserveDisplayIndex: boolean,
     * }} param0
     */
    recalculateDisplayPoses({
        sortPosesBy, bestPoseOrderField, displayFilters={}, preserveDisplayIndex=false,
    }) {
        const {
            frag: oldSelectedFrag, displayIndex: oldDisplayIndex,
        } = this.selectedDisplayPose ?? {};

        const filtered = this.filterPoses(this.frags, displayFilters);
        const sorted = sortFrags(filtered, { sortPosesBy, bestPoseOrderField });
        this.assignDisplayPoses(sorted);

        // Update selectedIndex if necessary
        if (!oldSelectedFrag) {
            // No previously selected frag, so just try to select the first one
            this.selectedIndex = this.displayPoses[0]?.fragIndex ?? -1;
        } else if (!filtered.includes(oldSelectedFrag)) {
            // No longer have the previously selected frag, but try to use the old display index
            let newDisplayIndex = oldDisplayIndex;
            if (newDisplayIndex >= this.displayPoses.length) {
                newDisplayIndex = this.displayPoses.length - 1;
            }
            this.selectedIndex = this.displayPoses[newDisplayIndex]?.fragIndex ?? -1;
        }

        if (preserveDisplayIndex) {
            // Try to preserve the display index, but if it doesn't exist, select the first one
            const newDisplayPose = this.displayPoses[oldDisplayIndex];
            if (newDisplayPose) {
                this.selectedIndex = newDisplayPose.fragIndex;
            } else {
                this.selectedIndex = this.displayPoses[0]?.fragIndex ?? -1;
            }
        }
    }

    /**
     *
     * @param {DecodedFragment[]} poses
     * @param {{[propName: string]: PosePropertyFilter}} filters
     * @returns {DecodedFragment[]}
     */
    filterPoses(poses, filters={}) {
        return Object.values(filters).reduce((acc, next) => acc.filter(next.filter), poses);
    }

    /**
     * @param {DecodedFragment[]} orderedFrags
     * @returns {DisplayPose[]}
     */
    assignDisplayPoses(orderedFrags) {
        this.displayPoses = orderedFrags.map((frag, displayIndex) => {
            const fragIndex = this.frags.findIndex((f) => f === frag);
            return { frag, fragIndex, displayIndex };
        });
    }

    updatePerCaseInfo() {
        this.refBestPoseInfo = null;
        for (const [projectCase, poseInfo] of Object.entries(this.suggestion.projectCaseDetails)) {
            const newInfo = new AnnotatedBestPoseInfo(poseInfo.bestPoseInfo, projectCase, this);
            if (!this.refBestPoseInfo) {
                this.refBestPoseInfo = newInfo;
            }
            this.perCaseInfo[projectCase] = newInfo;
        }
    }

    getSummaryInfo(thresholdInfo=new ActivityThresholdInfo()) {
        const { bestPoseOrderField, activityField, activityThreshold } = thresholdInfo;
        const activeTargets = [];
        const activeOffTargets = [];
        const inactiveTargets = [];
        const inactiveOffTargets = [];

        for (const projectCase of this.projectCases) {
            const isTarget = this.perCaseInfo[projectCase].targetInfo.isTarget;
            const bindingInfo = FragmentSearchResult.findBestPose(this.suggestion.frags, {
                projectCase, bestPoseOrderField,
            });
            if (bindingInfo) {
                let arrayToUse;
                if (bindingInfo.meetsThreshold(activityThreshold, activityField)) {
                    arrayToUse = isTarget ? activeTargets : activeOffTargets;
                } else {
                    arrayToUse = isTarget ? inactiveTargets : inactiveOffTargets;
                }
                arrayToUse.push({ projectCase, bindingInfo });
            } else {
                // What should we do about the no-data cases?
            }
        }

        return {
            activeTargetCount: activeTargets.length,
            activeOffTargetCount: activeOffTargets.length,
            selectivityScore: (
                activeTargets.length + inactiveOffTargets.length - activeOffTargets.length
            ),
            selectivityRatio: (
                (activeTargets.length + inactiveOffTargets.length) / this.projectCases.length
            ),
        };
    }

    /** @param {ActivityThresholdInfo?} */
    updateSummaryInfo(thresholdInfo) {
        this.summaryInfo = this.getSummaryInfo(thresholdInfo);
    }
}

/**
 * BestPoseBindingInfo objects are returned from search results.
 * This class wraps them to include mapCase and target info and provide helpers.
 */
class AnnotatedBestPoseInfo {
    /**
     * @param {BestPoseBindingInfo} bestPoseInfo
     * @param {string} projectCase
     */
    constructor(bestPoseInfo, projectCase, suggestion) {
        this.bestPoseInfo = bestPoseInfo;
        this.projectCase = projectCase;
        this.suggestion = suggestion;
        const { mapCase, targetInfo } = getMapCaseAndTargetInfo(projectCase);
        this.mapCase = mapCase;
        this.targetInfo = targetInfo;
    }

    isActive({ activityThreshold, activityField }=new ActivityThresholdInfo()) {
        return this.bestPoseInfo?.meetsThreshold(activityThreshold, activityField) || false;
    }

    isActiveTarget(thresholdInfo) {
        return this.targetInfo.isTarget && this.isActive(thresholdInfo);
    }

    isActiveOffTarget(thresholdInfo) {
        return !this.targetInfo.isTarget && this.isActive(thresholdInfo);
    }

    get bindingScore() { return this.bestPoseInfo?.bindingScore; }
    get ligandEfficiency() { return this.bestPoseInfo?.ligandEfficiency; }
    get enSolute() { return this.bestPoseInfo?.enSolute; }
    get ligandEfficiencyEn() { return this.bestPoseInfo?.ligandEfficiencyEn; }
    get enSolv() { return this.bestPoseInfo?.enSolv; }
    get exchemPotential() { return this.bestPoseInfo?.exchemPotential; }

    get poseStatistics() {
        const underlyingSuggestion = this.suggestion.suggestion;
        return underlyingSuggestion.projectCaseDetails[this.projectCase]?.poseStatistics ?? {};
    }

    get bindingScoreRatio() {
        const myValue = this.bindingScore;
        const refValue = this.suggestion.refBestPoseInfo?.bindingScore;
        if (myValue == null || refValue == null) {
            return null;
        }
        return myValue / refValue;
    }

    filterPoses(projectCase, inclusiveMin, exclusiveMax) {
        return this.suggestion.frags.filter((frag) => {
            const bindingScore = BestPoseBindingInfo.getBindingScore(frag.exchemPotential);
            return frag.projectCase === projectCase
                && (exclusiveMax == null || bindingScore < exclusiveMax)
                && (inclusiveMin == null || bindingScore >= inclusiveMin);
        });
    }

    bucketCount(projectCase, inclusiveMin, exclusiveMax) {
        if (this.bindingScore == null) return null;
        return this.filterPoses(projectCase, inclusiveMin, exclusiveMax).length;
    }

    get poseBucket0_1() { return this.bucketCount(this.projectCase, -1, 0); }
    get poseBucket1_2() { return this.bucketCount(this.projectCase, -2, -1); }
    get poseBucket2_3() { return this.bucketCount(this.projectCase, -3, -2); }
    get poseBucket3_4() { return this.bucketCount(this.projectCase, -4, -3); }
    get poseBucket4_5() { return this.bucketCount(this.projectCase, -5, -4); }
    get poseBucket5_6() { return this.bucketCount(this.projectCase, -6, -5); }
    get poseBucket6_7() { return this.bucketCount(this.projectCase, -7, -6); }
    get poseBucket7_8() { return this.bucketCount(this.projectCase, -8, -7); }
    get poseBucket8_9() { return this.bucketCount(this.projectCase, -9, -8); }
    get poseBucket9_10() { return this.bucketCount(this.projectCase, -10, -9); }
    get poseBucket10_11() { return this.bucketCount(this.projectCase, -11, -10); }
    get poseBucket11_12() { return this.bucketCount(this.projectCase, -12, -11); }
    get poseBucket12_13() { return this.bucketCount(this.projectCase, -13, -12); }
    get poseBucket13_14() { return this.bucketCount(this.projectCase, -14, -13); }
    get poseBucket14_15() { return this.bucketCount(this.projectCase, -15, -14); }
    get poseBucket15_16() { return this.bucketCount(this.projectCase, -16, -15); }
    get poseBucket16_17() { return this.bucketCount(this.projectCase, -17, -16); }
    get poseBucket17_18() { return this.bucketCount(this.projectCase, -18, -17); }
    get poseBucket18_19() { return this.bucketCount(this.projectCase, -19, -18); }
    get poseBucket19_20() { return this.bucketCount(this.projectCase, -20, -19); }
    get poseBucketX_0() { return this.bucketCount(this.projectCase, 0, undefined); }
    get poseBucket20_X() { return this.bucketCount(this.projectCase, undefined, -20); }
}

/**
 * This class manages thresholds for determining pose activity.
 * The values will be passed into `BestPoseBindingInfo.meetsThreshold()`.
 */
export class ActivityThresholdInfo {
    static get DefaultBindingScoreThreshold() { return -7.0; }
    static get DefaultEnSoluteThreshold() { return -19.0; }

    constructor(thresholdInfo={}) {
        const { bestPoseOrderField='bindingScore', activityField='bindingScore' } = thresholdInfo;
        let { activityThreshold } = thresholdInfo;
        if (activityThreshold == null) {
            const defaults = {
                bindingScore: ActivityThresholdInfo.DefaultBindingScoreThreshold,
                enSolute: ActivityThresholdInfo.DefaultEnSoluteThreshold,
            };
            activityThreshold = defaults[activityField] ?? 0;
        }
        this.bestPoseOrderField = bestPoseOrderField;
        this.activityField = activityField;
        this.activityThreshold = activityThreshold;
    }
}
